/**
 * AI CONTEXT
 * Purpose: derives the standout-moments student list — membership (roster + makeups + tryouts),
 * present/absent split, selected-session-first sort, name search, and display cap.
 * Inputs: TeacherViewContext sessions/exceptions + live student atoms + standout moments.
 * Used by: TeacherClassInfo StandoutMoments.
 */
import { countBy, keyBy, orderBy, uniq } from "lodash"
import { useMemo } from "react"

import { standoutMomentAtoms } from "#atoms"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { Attendance, AttendanceStatus } from "#customTypes/types"
import { useGetSomeStudents } from "#hooks/jotai/useGetStudents"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import { getSessionInfoFromId } from "#utils/classHelpers"
import { getCurrentSemester } from "#utils/semesterHelpers"

import { countsAsInClass, entryForSelectedSession } from "./teacherStorySession"
import { useTeacherViewContext } from "./TeacherViewContextProvider"

export const STANDOUT_LIST_CAP = 10

export type StandoutStudentSource = "roster" | "makeup" | "tryout"

export interface StandoutStudentItem {
  id: string
  studentId: Uuid
  name: string
  momentCount: number
  isPresent: boolean
  isInSelectedSession: boolean
  source: StandoutStudentSource
  classId: Uuid | undefined
  curriculumId: Uuid | undefined
  // The primary session's project — stamped onto saved moments so the
  // Standout Review dashboard never has to infer it for new data.
  projectId: Uuid | undefined
  // The row's class session. Sorting and the project stamp use it.
  sessionId: Uuid | undefined
  // Selected session when this student counts as in class there. The story
  // attaches to it without changing attendance. Absent students leave this off
  // and the panel uses their nearest attended session instead.
  inClassSessionId: Uuid | undefined
}

// Makeup kids' status lives on the missed session. Roster MAKEUP_* means they
// scheduled a makeup elsewhere, so that is an absence in this class.
const getAttendanceStatus = (
  attendance: Attendance,
  sessionId: Uuid
): AttendanceStatus => {
  const [classId, sessionIndex] = getSessionInfoFromId(sessionId)
  return (
    attendance?.[classId]?.[sessionIndex]?.status ?? ATTENDANCE_STATUSES.INVALID
  )
}

interface StudentSessionEntry {
  sessionId: Uuid
  classId: Uuid
  name: string
  isInClass: boolean
  source: StandoutStudentSource
}

export const useStandoutStudentList = ({
  searchTerm,
  showAbsent,
  showAll,
}: {
  searchTerm: string
  showAbsent: boolean
  showAll: boolean
}) => {
  const {
    sessionsForCurrentUser,
    selectedSession,
    studentsByExceptionType,
    classesById,
  } = useTeacherViewContext()

  const semester = useMemo(() => getCurrentSemester(), [])

  // Read students live from the atoms rather than through context snapshots so
  // present/absent recomputes the moment attendance changes elsewhere.
  const candidateStudentIds = useMemo(() => {
    const ids = sessionsForCurrentUser.flatMap(
      ({ session }) => session.studentIds ?? []
    )
    for (const exception of studentsByExceptionType.makeups) {
      ids.push(exception.student.id)
    }
    for (const exception of studentsByExceptionType.tryouts) {
      ids.push(exception.student.id)
    }
    return uniq(ids).sort()
  }, [sessionsForCurrentUser, studentsByExceptionType])

  const liveStudents = useGetSomeStudents(candidateStudentIds)
  const studentsById = useMemo(() => keyBy(liveStudents, "id"), [liveStudents])

  const momentQuery = useMemo(
    () => ({
      queries: {
        semester: { value: semester, comparator: COMPARATORS.EQUALS },
      },
    }),
    [semester]
  )

  const allMoments = useFilteredDocs<StandoutMomentConstructor>(
    standoutMomentAtoms,
    DOC_TYPES.STANDOUT_MOMENTS,
    momentQuery
  )

  const entriesByStudent = useMemo(() => {
    const map: Record<Uuid, StudentSessionEntry[]> = {}
    const push = (studentId: Uuid, entry: StudentSessionEntry) => {
      if (!map[studentId]) map[studentId] = []
      map[studentId].push(entry)
    }

    for (const { session } of sessionsForCurrentUser) {
      for (const studentId of session.studentIds ?? []) {
        const student = studentsById[studentId]
        const status = student
          ? getAttendanceStatus(student.attendance ?? {}, session.id)
          : ATTENDANCE_STATUSES.INVALID
        push(studentId, {
          sessionId: session.id,
          classId: session.classId,
          name: student
            ? `${student.firstName} ${student.lastName}`
            : studentId,
          isInClass: countsAsInClass("roster", status),
          source: "roster",
        })
      }
    }

    for (const exception of studentsByExceptionType.makeups) {
      const { sessionId, classId, exceptionData } = exception
      const student = studentsById[exception.student.id] ?? exception.student
      // exceptionData is a MakeupConstructor for makeup exceptions, but the
      // union with TryoutConstructor (plus the DatabaseObject index signature)
      // prevents narrowing — read the makeup fields defensively instead.
      const missedSessionId = exceptionData?.missedSessionId
      if (typeof missedSessionId !== "string") continue
      // Next-semester makeups are hidden entirely: the kid isn't in this
      // class's cohort and their evals are already over.
      if (exceptionData?.isScheduledForNextSemester === true) continue
      const status = getAttendanceStatus(
        student.attendance ?? {},
        missedSessionId
      )
      push(student.id, {
        sessionId,
        classId,
        name: `${student.firstName} ${student.lastName}`,
        isInClass: countsAsInClass("makeup", status),
        source: "makeup",
      })
    }

    for (const exception of studentsByExceptionType.tryouts) {
      const { sessionId, classId } = exception
      const student = studentsById[exception.student.id] ?? exception.student
      const status = getAttendanceStatus(student.attendance ?? {}, sessionId)
      push(student.id, {
        sessionId,
        classId,
        name: `${student.firstName} ${student.lastName}`,
        isInClass: countsAsInClass("tryout", status),
        source: "tryout",
      })
    }

    return map
  }, [sessionsForCurrentUser, studentsById, studentsByExceptionType])

  const selectedSessionId = selectedSession?.id

  const sessionsById = useMemo(
    () =>
      keyBy(
        sessionsForCurrentUser.map(({ session }) => session),
        "id"
      ),
    [sessionsForCurrentUser]
  )

  const allItems = useMemo((): StandoutStudentItem[] => {
    const countsByStudent = countBy(allMoments, "studentId")

    const items = Object.entries(entriesByStudent).map(
      ([studentId, entries]): StandoutStudentItem => {
        const selectedEntry = entryForSelectedSession(
          entries,
          selectedSessionId
        )
        // The in-class entry for the selected session drives the story and
        // which class it is filed under. An absent roster row does not hide
        // a makeup or tryout on that same session.
        const primary = selectedEntry ?? entries[0]

        return {
          id: studentId,
          studentId,
          name: primary.name,
          momentCount: countsByStudent[studentId] ?? 0,
          isPresent: entries.some((entry) => entry.isInClass),
          isInSelectedSession: !!selectedEntry,
          source: primary.source,
          classId: primary.classId,
          curriculumId: classesById[primary.classId]?.curriculumId,
          projectId: sessionsById[primary.sessionId]?.projectId,
          sessionId: primary.sessionId,
          inClassSessionId: selectedEntry?.isInClass
            ? selectedEntry.sessionId
            : undefined,
        }
      }
    )

    return orderBy(
      items,
      [(item) => (item.isInSelectedSession ? 0 : 1), "momentCount", "name"],
      ["asc", "asc", "asc"]
    )
  }, [
    allMoments,
    entriesByStudent,
    selectedSessionId,
    classesById,
    sessionsById,
  ])

  return useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    const isSearching = normalizedSearch.length > 0

    const matches = isSearching
      ? allItems.filter((item) =>
          item.name.toLowerCase().includes(normalizedSearch)
        )
      : allItems

    const present = matches.filter((item) => item.isPresent)
    // Searching intentionally ignores the absent toggle — if you're looking
    // for a kid by name, you want to find them either way.
    const absent =
      showAbsent || isSearching ? matches.filter((item) => !item.isPresent) : []

    const eligibleCount = present.length + absent.length

    if (isSearching || showAll) {
      return {
        presentItems: present,
        absentItems: absent,
        hiddenCount: 0,
        eligibleCount,
        totalCount: allItems.length,
        isSearching,
        semester,
      }
    }

    const presentItems = present.slice(0, STANDOUT_LIST_CAP)
    const absentItems = absent.slice(0, STANDOUT_LIST_CAP - presentItems.length)

    return {
      presentItems,
      absentItems,
      hiddenCount: eligibleCount - presentItems.length - absentItems.length,
      eligibleCount,
      totalCount: allItems.length,
      isSearching,
      semester,
    }
  }, [allItems, searchTerm, showAbsent, showAll, semester])
}
